import constants from '../../../Constants';

import choiceParser from './ChoiceParser';
import customGeogebraParser from './CustomGeogebraParser';
import customOralParser from './CustomOralParser';
import extendedTextParser from './ExtendedTextParser';
import codeCompilerParser from './ExtendedTextCodeParser';
import gapMatchParser from './GapMatchParser';
import graphicGapMatchParser from './GraphicGapMatchParser';
import dragAndDropParser from './DragAndDropParser';
import hotSpotParser from './HotSpotParser';
import inlineChoiceParser from './InlineChoiceParser';
import matchParser from './MatchParser';
import textEntryParser from './TextEntryParser';
import uploadParser from './UploadParser';
import pciParser from './PCIParser';

const interactionParser = {
    getTargetForInteraction(questionIdentifier, interaction) {
        return `${questionIdentifier}_${interaction.getAttribute('responseIdentifier')}`;
    },

    getInteractionForIdentifier(responseIdentifier, itemBodyElement) {
        // return itemBodyElement.querySelectorAll('[responseIdentifier='+responseIdentifier+']'); probably would have worked in browser
        const elements = itemBodyElement.getElementsByTagName('*');
        for (const element of Array.from(elements)) {
            if (element.getAttribute('responseIdentifier') === responseIdentifier) {
                return element;
            }
        }

        return undefined;
    },

    parseCustomInteraction(interaction, language, defaultLanguage, responseDeclarations, templateDeclarations) {
        const insperaVariant = interaction.getAttribute('inspera:variant');
        if (insperaVariant === 'geogebra') {
            return customGeogebraParser.parse(interaction, language, defaultLanguage, responseDeclarations);
        }
        if (insperaVariant === 'oral') {
            return customOralParser.parse(interaction, language, defaultLanguage);
        }

        const isPCI = interaction.getElementsByTagName('portableCustomInteraction').length > 0;
        if (isPCI) {
            return pciParser.parse(interaction, language, defaultLanguage, responseDeclarations, templateDeclarations);
        }

        return {};
    },

    parseExtendedTextVariant(interaction, language, defaultLanguage, responseDeclarations) {
        const insperaVariant = interaction.getAttribute('inspera:variant');
        if (insperaVariant === 'code' || insperaVariant === 'code-compiler') {
            return codeCompilerParser.parse(interaction, language, defaultLanguage, responseDeclarations);
        }
        return extendedTextParser.parse(interaction, language, defaultLanguage, responseDeclarations);
    },

    /**
     * Finds the interaction in the itemBodyElement based on the responseIdentifier and merges responseDeclaration with target, type
     * in addition to interaction specific data
     * @param questionIdentifier
     * @param declaration
     * @param itemBodyElement
     * @param language
     * @param defaultLanguage
     * @param responseDeclarations
     * @param className
     * @param assessmentRunId - optional
     * @param templateDeclarations - optional
     * @param version - optional
     * @returns {*}
     */
    parseInteraction({
        questionIdentifier,
        declaration,
        itemBodyElement,
        language,
        defaultLanguage,
        responseDeclarations,
        className,
        assessmentRunId,
        templateDeclarations,
        version,
        mediaInfo,
    }) {
        const interactionNode = this.getInteractionForIdentifier(declaration.identifier, itemBodyElement);
        if (interactionNode === undefined) {
            return undefined; // interaction not found in itemBodyElement
        }

        const type = interactionNode.nodeName;
        const model = Object.assign({}, declaration, {
            target: this.getTargetForInteraction(questionIdentifier, interactionNode),
            type,
            classNames: className,
        });
        const supportedLanguages = itemBodyElement.getAttribute(constants.supportedLanguagesAttr);
        switch (type) {
        case constants.interactionType.CHOICE_INTERACTION:
            return Object.assign(
                model,
                choiceParser.parse(interactionNode, language, defaultLanguage, `Question_${questionIdentifier}_`,
                    assessmentRunId, mediaInfo, supportedLanguages));

        case constants.interactionType.CUSTOM_INTERACTION:
            return Object.assign(
                model,
                this.parseCustomInteraction(interactionNode, language, defaultLanguage, responseDeclarations, templateDeclarations));

        case constants.interactionType.DRAG_AND_DROP_INTERACTION:
            if (version === '2.1' || interactionNode.getAttribute('inspera:useQTIv2p1') === 'true') {
                // D&D is not compatible with QTI2.2
                return Object.assign(
                    model,
                    dragAndDropParser.parse(interactionNode, language, defaultLanguage, responseDeclarations,
                        assessmentRunId, questionIdentifier, mediaInfo)
                );
            }

            return Object.assign(
                model,
                graphicGapMatchParser.parse(interactionNode, language, defaultLanguage, responseDeclarations,
                    assessmentRunId, questionIdentifier, mediaInfo)
            );

        case constants.interactionType.GAP_MATCH_INTERACTION:
            return Object.assign(
                model,
                gapMatchParser.parse(interactionNode, language, defaultLanguage, assessmentRunId, questionIdentifier, mediaInfo)
            );

        case constants.interactionType.EXTENDED_TEXT_INTERACTION:
            return Object.assign(
                model,
                this.parseExtendedTextVariant(interactionNode, language, defaultLanguage, responseDeclarations));

        case constants.interactionType.HOTSPOT_INTERACTION:
            return Object.assign(
                model,
                hotSpotParser.parse(interactionNode, language, defaultLanguage, responseDeclarations, assessmentRunId, mediaInfo)
            );

        case constants.interactionType.INLINE_CHOICE_INTERACTION:
            return Object.assign(
                model,
                inlineChoiceParser.parse(interactionNode, language, defaultLanguage));

        case constants.interactionType.MATCH_INTERACTION:
            return Object.assign(
                model,
                matchParser.parse(interactionNode, language, defaultLanguage, assessmentRunId));

        case constants.interactionType.TEXT_ENTRY_INTERACTION:
            return Object.assign(
                model,
                textEntryParser.parse(interactionNode, language, defaultLanguage));

        case constants.interactionType.UPLOAD_INTERACTION:
            return Object.assign(
                model,
                uploadParser.parse(interactionNode, language, defaultLanguage));

        default:
            return model;
        }
    },

    /**
     * Loops through the responseDeclarations and calls parsing of each interaction
     * @param {object} params
     * @param {number} params.questionIdentifier
     * @param {array} params.responseDeclarations
     * @param {Element} params.itemBodyElement
     * @param {string} params.language
     * @param {string} params.className
     * @param {number} params.assessmentRunId - optional
     * @param {array} params.templateDeclarations - optional
     * @param {string} params.version - optional
     * @param {object} params.mediaInfo - optional
     * @returns {Array}
     */
    getInteractions({
        questionIdentifier,
        responseDeclarations,
        itemBodyElement,
        language,
        className,
        assessmentRunId,
        templateDeclarations,
        version,
        mediaInfo,
    }) {
        const models = [];
        const defaultLanguage = itemBodyElement.getAttribute(constants.defaultLanguageAttr);

        for (const declaration of responseDeclarations) {
            const interaction = this.parseInteraction({
                questionIdentifier,
                declaration,
                itemBodyElement,
                language,
                defaultLanguage,
                responseDeclarations,
                className,
                assessmentRunId,
                templateDeclarations,
                version,
                mediaInfo,
            });

            if (interaction) {
                models.push(interaction);
            }
        }
        return models;
    },

};

export default interactionParser;
